import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { productAPI, categoryAPI } from '../../utils/api';
import { Search, ArrowLeft, ArrowRight, Star } from 'lucide-react';

const SORT_OPTIONS = [
  { value: 'created_at', label: 'Më të fundit' },
  { value: 'price', label: 'Çmimi' },
  { value: 'rating', label: 'Vlerësimi' },
  { value: 'name', label: 'Emri' },
];

function ProductCard({ product }) {
  const images = (() => { try { return JSON.parse(product.images || '[]'); } catch { return [product.images]; } })();
  const price = Number(product.price) || 0;
  const comparePrice = Number(product.compare_price) || 0;
  const discount = comparePrice > 0 ? Math.round((1 - price / comparePrice) * 100) : 0;

  return (
    <Link to={`/product/${product.slug}`} className="product-card">
      <div className="product-card-media">
        {comparePrice > 0 && <span className="product-card-badge">-{discount}%</span>}
        {product.is_featured && <span className="product-card-tag">⭐ Bestseller</span>}
        <img src={images[0] || 'https://via.placeholder.com/280x280?text=Produkt'} alt={product.name} />
      </div>
      <div className="product-card-body">
        <div className="product-card-category">{product.category_name || 'Pa kategori'}</div>
        <h3>{product.name}</h3>
        <div className="product-card-meta">
          <span>{'★'.repeat(Math.round(product.avg_rating || 5))}{'☆'.repeat(5 - Math.round(product.avg_rating || 5))}</span>
          <span>{product.review_count || 0} recensione</span>
        </div>
        <div className="product-card-price">
          <span>${price.toFixed(2)}</span>
          {comparePrice > 0 && <small>${comparePrice.toFixed(2)}</small>}
        </div>
      </div>
    </Link>
  );
}

export default function ShopPage() {
  const [params, setParams] = useSearchParams();
  const [searchInput, setSearchInput] = useState(params.get('search') || '');

  const page = parseInt(params.get('page') || '1', 10);
  const category = params.get('category') || '';
  const featured = params.get('featured') === 'true';
  const search = params.get('search') || '';
  const sort = params.get('sort') || 'created_at';
  const order = params.get('order') || 'DESC';

  useEffect(() => {
    setSearchInput(search);
  }, [search]);

  const updateParams = (changes) => {
    const next = new URLSearchParams(params);
    Object.entries(changes).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') {
        next.delete(key);
      } else {
        next.set(key, value);
      }
    });
    if (!('page' in changes)) {
      next.set('page', '1');
    }
    setParams(next);
  };

  const { data: categories = [] } = useQuery({
    queryKey: ['shop-categories'],
    queryFn: () => categoryAPI.getAll().then((res) => res.data),
  });

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['shop-products', category, featured, search, sort, order, page],
    queryFn: () => productAPI.getAll({
      category: category || undefined,
      featured: featured ? 'true' : undefined,
      search: search || undefined,
      sort,
      order,
      page,
      limit: 16,
    }).then((res) => res.data),
    keepPreviousData: true,
  });

  const products = data?.products || [];
  const pagination = data?.pagination || { page: 1, pages: 1, total: 0 };

  const subtitle = featured ? 'Produktet më të mira të zgjedhura për ju' : 'Zgjidhni nga koleksioni ynë i plotë i natyrshëm.';

  const hasFilters = category || featured;
  const activeCategoryLabel = category ? categories.find((c) => c.slug === category)?.name : 'Të gjitha';

  return (
    <div className="page-container" style={{ paddingTop: 80, paddingBottom: 80 }}>
      <div className="page-header">
        <div>
          <p className="section-eyebrow">Dyqan</p>
          <h1 className="page-title">Produktet tona</h1>
          <p className="page-sub">{subtitle}</p>
        </div>
      </div>

      <div className="shop-grid">
        <aside className="shop-sidebar">
          <div className="shop-panel">
            <div className="panel-title">Kërkimi</div>
            <div className="search-bar">
              <Search size={16} />
              <input
                className="form-input"
                placeholder="Kërko produkt..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && updateParams({ search: searchInput || undefined })}
              />
              {searchInput && (
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => updateParams({ search: undefined })}>X</button>
              )}
            </div>
            <button className="btn btn-primary btn-full" type="button" onClick={() => updateParams({ search: searchInput || undefined })}>
              Kërko
            </button>
          </div>

          <div className="shop-panel">
            <div className="panel-title">Kategoritë</div>
            <div className="category-list">
              <button className={`category-chip${!category ? ' active' : ''}`} onClick={() => updateParams({ category: undefined })}>Të gjitha</button>
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  className={`category-chip${category === cat.slug ? ' active' : ''}`}
                  onClick={() => updateParams({ category: cat.slug })}
                >
                  {cat.name}
                </button>
              ))}
            </div>
          </div>

          <div className="shop-panel">
            <div className="panel-title">Filter</div>
            <button
              className={`btn btn-ghost btn-full${featured ? ' active' : ''}`}
              type="button"
              onClick={() => updateParams({ featured: featured ? undefined : 'true' })}
            >
              {featured ? 'Shfaq të gjitha produktet' : 'Vetëm Bestseller'}
            </button>
            <div style={{ marginTop: 12 }}>
              <div className="panel-title">Renditja</div>
              <select className="form-input" value={sort} onChange={(e) => updateParams({ sort: e.target.value })}>
                {SORT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
              <select className="form-input" style={{ marginTop: 12 }} value={order} onChange={(e) => updateParams({ order: e.target.value })}>
                <option value="DESC">Zbritëse</option>
                <option value="ASC">Rritëse</option>
              </select>
            </div>
          </div>
        </aside>

        <section className="shop-main">
          <div className="shop-headline">
            <div>
              <h2>{featured ? 'Bestseller' : 'Të gjitha produktet'}</h2>
              <p style={{ color: 'var(--text-light)' }}>{products.length} produkt{products.length === 1 ? '' : 'e'} nga {pagination.total}</p>
              {hasFilters && <p style={{ marginTop: 8, color: 'var(--text-light)' }}>Filtruar sipas: {featured ? 'Bestseller' : ''} {category ? `| ${activeCategoryLabel}` : ''}</p>}
            </div>
            {isFetching && <span style={{ color: 'var(--green-mid)', fontWeight: 700 }}>Duke ngarkuar...</span>}
          </div>

          {isLoading ? (
            <div className="loading-screen" style={{ minHeight: 260 }}><div className="spinner" /></div>
          ) : products.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">🛒</div>
              <h3>Nuk u gjetën produkte</h3>
              <p>Provoni të ndryshoni fjalën kyçe ose të hiqni filtrat.</p>
              <button className="btn btn-primary" type="button" onClick={() => setParams(new URLSearchParams())}>Pastro Filtrat</button>
            </div>
          ) : (
            <div className="shop-products-grid">
              {products.map((product) => <ProductCard key={product.id} product={product} />)}
            </div>
          )}

          {pagination.pages > 1 && (
            <div className="pagination-row">
              <button className="btn btn-ghost" type="button" onClick={() => updateParams({ page: Math.max(1, page - 1).toString() })} disabled={page <= 1}><ArrowLeft size={16} /></button>
              {Array.from({ length: pagination.pages }, (_, index) => (
                <button
                  key={index}
                  className={`btn btn-sm${page === index + 1 ? ' btn-primary' : ' btn-ghost'}`}
                  type="button"
                  onClick={() => updateParams({ page: (index + 1).toString() })}
                >
                  {index + 1}
                </button>
              ))}
              <button className="btn btn-ghost" type="button" onClick={() => updateParams({ page: Math.min(pagination.pages, page + 1).toString() })} disabled={page >= pagination.pages}><ArrowRight size={16} /></button>
            </div>
          )}
        </section>
      </div>

      <style>{`
        .shop-grid { display: grid; grid-template-columns: 280px 1fr; gap: 24px; }
        .shop-sidebar { display: flex; flex-direction: column; gap: 20px; }
        .shop-panel { background: var(--white); border-radius: var(--radius-xl); padding: 20px; border: 1px solid var(--cream-dark); }
        .panel-title { font-weight: 700; margin-bottom: 14px; color: var(--green-deep); }
        .search-bar { display: flex; align-items: center; gap: 10px; border: 1.5px solid var(--cream-dark); border-radius: var(--radius-md); padding: 10px 12px; }
        .search-bar input { border: none; outline: none; width: 100%; background: transparent; }
        .category-list { display: grid; gap: 10px; }
        .category-chip { border: 1px solid var(--cream-dark); background: white; color: var(--text-dark); border-radius: var(--radius-full); padding: 10px 14px; text-align: left; cursor: pointer; transition: all .2s; }
        .category-chip.active, .category-chip:hover { border-color: var(--green-mid); background: var(--green-soft); }
        .shop-main { display: flex; flex-direction: column; gap: 24px; }
        .shop-headline { display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 16px; }
        .shop-products-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 22px; }
        .product-card { display: block; border-radius: var(--radius-xl); overflow: hidden; background: white; text-decoration: none; color: inherit; border: 1px solid var(--cream-dark); transition: transform .2s, box-shadow .2s; }
        .product-card:hover { transform: translateY(-4px); box-shadow: var(--shadow-md); }
        .product-card-media { position: relative; background: var(--cream); min-height: 240px; display: flex; align-items: center; justify-content: center; padding: 16px; }
        .product-card-media img { max-width: 100%; max-height: 100%; object-fit: contain; }
        .product-card-badge, .product-card-tag { position: absolute; top: 14px; left: 14px; background: var(--gold); color: var(--green-deep); padding: 6px 12px; border-radius: var(--radius-full); font-size: .75rem; font-weight: 700; }
        .product-card-tag { left: auto; right: 14px; background: var(--green-light); color: white; }
        .product-card-body { padding: 18px; display: flex; flex-direction: column; gap: 12px; }
        .product-card-category { font-size: .75rem; color: var(--text-light); text-transform: uppercase; letter-spacing: .08em; }
        .product-card-body h3 { font-size: 1rem; margin: 0; }
        .product-card-meta { display: flex; justify-content: space-between; align-items: center; color: var(--gold); font-size: .85rem; }
        .product-card-price { display: flex; align-items: baseline; gap: 10px; font-weight: 700; }
        .product-card-price small { font-size: .82rem; color: var(--text-light); text-decoration: line-through; }
        .pagination-row { display: flex; flex-wrap: wrap; gap: 8px; justify-content: center; align-items: center; margin-top: 10px; }
        .empty-state { text-align: center; background: var(--white); border-radius: var(--radius-xl); padding: 60px 24px; border: 1px solid var(--cream-dark); }
        .empty-state-icon { font-size: 3rem; margin-bottom: 16px; }
        @media(max-width: 1100px) { .shop-products-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
        @media(max-width: 880px) { .shop-grid { grid-template-columns: 1fr; } .shop-sidebar { order: 2; } }
        @media(max-width: 680px) { .shop-products-grid { grid-template-columns: 1fr; } }
      `}</style>
    </div>
  );
}
